import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import { NavbarResponse, getNavbar } from '../../apis/navbar'
import Typography from '@Components/typo'
import PageLayout from '@Components/layout/pageLayout'
import Image from 'next/image'
import {
  ChargeAtHomePageResponse,
  chargeAtHomePages,
} from '../../apis/chargeAtHomePages'
import { Button } from '@Components/button'
import { useState } from 'react'
import { FooterResponse, getFooter } from '../../apis/footer'
import { Seo } from '@Components/seo'
import getSanitizedContent from '../../utils/getSanitizeHtml'

interface ChageAtHomePage {
  navbar: NavbarResponse
  footer: FooterResponse
  chargeAtHomePages: {
    base: ChargeAtHomePageResponse
  }
}

const statusOfButton = {
  Installation: 'installation',
  HowToCharge: 'how-to-charge',
}

const ChargeAtHomePage = ({
  chargeAtHomePages,
  navbar,
  footer,
}: ChageAtHomePage) => {
  const [selected, setSelected] = useState<string>(statusOfButton.Installation)
  const { base } = chargeAtHomePages
  const sanitizedBaseChargeStartContent = getSanitizedContent(
    base.ChargeStartContent
  )
  const sanitizedBaseChargeStopContent = getSanitizedContent(
    base.ChargeStopContent
  )
  return (
    <>
      <Seo
        title={base?.Seo?.MetaTitle || 'Charge At Home | On-ion website'}
        description={base?.Seo?.MetaDescription || ''}
        keywords={base?.Seo?.Keywords || ''}
        canonicalURL={base?.Seo?.CanonicalURL || ''}
      />
      <PageLayout
        navbar={navbar}
        footer={footer}
        className='flex flex-col bg-neutral-level-1-default'
      >
        <Typography size='h4' weight='bold'>
          {base.Title}
        </Typography>

        <div className='flex flex-col gap-16'>
          <div className='flex flex-row gap-2.5'>
            <Button
              variant={
                selected === statusOfButton.Installation
                  ? 'toggle-active'
                  : 'toggle-inactive'
              }
              onClick={() => setSelected(statusOfButton.Installation)}
            >
              {base.InstallationButtonText}
            </Button>
            <Button
              variant={
                selected === statusOfButton.HowToCharge
                  ? 'toggle-active'
                  : 'toggle-inactive'
              }
              onClick={() => setSelected(statusOfButton.HowToCharge)}
            >
              {base.HowToChargeButtonText}
            </Button>
          </div>
        </div>

        {selected === statusOfButton.Installation ? (
          <>
            {base.Installation.map((item, index) => {
              const sanitizedContent = getSanitizedContent(item.Content)
              return (
                <div key={index} className='flex flex-col gap-8 lg:flex-row'>
                  <div className='flex w-full flex-col justify-center gap-4 text-content-default lg:w-[35%]'>
                    <div className='h-[74px] w-[74px] rounded-[100px] bg-content-default p-[22px]'>
                      <Typography
                        size='h5'
                        weight='bold'
                        className='flex justify-center text-white'
                      >
                        {item.Order}
                      </Typography>
                    </div>
                    <Typography size='h6' weight='bold'>
                      {item.Title}
                    </Typography>
                    <div
                      className='prose'
                      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                    />
                  </div>
                  <div className='flex h-[469px] w-full justify-center md:w-[65%]'>
                    <Image
                      width={100}
                      height={100}
                      src={item.Image?.url}
                      className='max-h-[469px] w-full'
                      alt=''
                    />
                  </div>
                </div>
              )
            })}

            <div className='flex flex-col gap-8 rounded-2xl bg-neutral-level-2-default p-12'>
              <div className='flex flex-col gap-2.5'>
                <Typography size='h6' weight='bold'>
                  {base.DownloadTitle}
                </Typography>
                {base.FileLink && base.FileName && (
                  <Typography
                    element='a'
                    href={base.FileLink}
                    className='text-link-default'
                  >
                    {base.FileName}
                  </Typography>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className='mt-8 flex flex-col gap-16 text-content-default'>
            <div className='flex flex-col gap-4'>
              <Typography size='h6' weight='bold'>
                {base.ChargeStartTitle}
              </Typography>
              <div
                className='prose'
                dangerouslySetInnerHTML={{
                  __html: sanitizedBaseChargeStartContent,
                }}
              />
            </div>
            <div className='flex flex-col gap-4'>
              <Typography size='h6' weight='bold'>
                {base.ChargeStopTitle}
              </Typography>
              <div
                className='prose'
                dangerouslySetInnerHTML={{
                  __html: sanitizedBaseChargeStopContent,
                }}
              />
            </div>
          </div>
        )}
      </PageLayout>
    </>
  )
}

export default ChargeAtHomePage

export const getServerSideProps = async ({
  locale = 'en',
}: {
  locale: string
}) => {
  const { getChargeAtHomePages } = chargeAtHomePages
  const [navbar, base, footer] = await Promise.all([
    getNavbar({ locale }),
    getChargeAtHomePages({ locale }),
    getFooter({ locale }),
  ])

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      navbar: navbar[0],
      footer: footer[0],
      chargeAtHomePages: {
        base: {
          ...base[0],
          installation: base[0].Installation.sort((a, b) => a.Order - b.Order),
        },
      },
    },
  }
}
